import React, { useRef } from 'react';
import { TimelineContent } from './timeline-animation';

const REVEAL_VARIANTS = {
  visible: (i) => ({
    y: 0,
    opacity: 1,
    filter: 'blur(0px)',
    transition: {
      delay: i * 0.12,
      duration: 0.55,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
  hidden: {
    filter: 'blur(10px)',
    y: -20,
    opacity: 0,
  },
};

export function TestimonialsBento() {
  const containerRef = useRef(null);

  return (
    <div className="tb-wrapper" ref={containerRef}>
      <style>{`
        .tb-wrapper {
          position: relative;
          width: 100%;
          max-width: 1320px;
          margin: 0 auto;
          font-family: 'Bricolage Grotesque', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .tb-header {
          text-align: center;
          margin-bottom: clamp(32px, 5vh, 52px);
          max-width: 960px;
          margin-left: auto;
          margin-right: auto;
          padding: 0 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .tb-title {
          font-family: 'Bricolage Grotesque', sans-serif !important;
          font-size: clamp(2.4rem, 5.5vw, 4.5rem) !important;
          font-weight: 800 !important;
          color: #ffffff;
          line-height: 1.08 !important;
          letter-spacing: -0.035em !important;
          margin: 0 0 16px 0;
          text-align: center !important;
          text-transform: none !important;
        }

        .tb-title span {
          color: #ec2b28;
        }

        .tb-subtitle {
          font-family: 'Bricolage Grotesque', sans-serif !important;
          font-size: clamp(0.92rem, 1.25vw, 1.08rem) !important;
          font-weight: 400 !important;
          color: rgba(255, 255, 255, 0.65) !important;
          margin: 0 auto;
          line-height: 1.7 !important;
          text-align: center;
        }

        /* 3-Column Bento Grid */
        .tb-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          padding: 0 16px;
          min-height: 640px;
        }

        .tb-col {
          display: flex;
          flex-direction: column;
          gap: 12px;
          height: 100%;
        }

        /* Base Card Styling */
        .tb-card {
          position: relative;
          border-radius: 14px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
          transition: transform 0.25s ease, box-shadow 0.25s ease;
        }

        .tb-card:hover {
          transform: translateY(-3px);
        }

        /* ── CARD THEME 1: White with Grid Pattern ── */
        .tb-card-white {
          background-color: #ffffff;
          color: #0a0a0a;
          border: 1px solid #e5e7eb;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
        }

        .tb-grid-background {
          position: absolute;
          inset: 0;
          height: 55%;
          background-image: 
            linear-gradient(to right, #4f4f4f18 1px, transparent 1px),
            linear-gradient(to bottom, #4f4f4f18 1px, transparent 1px);
          background-size: 38px 38px;
          mask-image: radial-gradient(ellipse 90% 70% at 50% 0%, #000 65%, transparent 100%);
          -webkit-mask-image: radial-gradient(ellipse 90% 70% at 50% 0%, #000 65%, transparent 100%);
          pointer-events: none;
        }

        .tb-card-white .tb-quote {
          color: #111827;
          font-weight: 500;
        }

        .tb-card-white .tb-author-name {
          color: #000000;
        }

        .tb-card-white .tb-author-role {
          color: #6b7280;
        }

        .tb-card-white .tb-avatar-letter {
          background: #f3f4f6;
          color: #111827;
          border: 1px solid #e5e7eb;
        }

        /* ── CARD THEME 2: Vibrant Blue Card ── */
        .tb-card-blue {
          background-color: #1d61f2;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 0 10px 30px rgba(29, 97, 242, 0.25);
        }

        .tb-card-blue .tb-quote {
          color: #ffffff;
          font-weight: 500;
        }

        .tb-card-blue .tb-author-name {
          color: #ffffff;
        }

        .tb-card-blue .tb-author-role {
          color: rgba(255, 255, 255, 0.85);
        }

        .tb-card-blue .tb-avatar-letter {
          background: rgba(255, 255, 255, 0.22);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.35);
        }

        /* ── CARD THEME 3: Dark Center Stack Cards ── */
        .tb-card-dark {
          background-color: #111111;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.12);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
        }

        .tb-card-dark .tb-quote {
          color: #f3f4f6;
          font-weight: 400;
        }

        .tb-card-dark .tb-author-name {
          color: #ffffff;
        }

        .tb-card-dark .tb-author-role {
          color: #9ca3af;
        }

        .tb-card-dark .tb-avatar-letter {
          background: #1f1f23;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.14);
        }

        /* Typography & Layout inside cards */
        .tb-quote {
          font-size: clamp(0.88rem, 1.05vw, 0.96rem);
          line-height: 1.6;
          margin: 0 0 20px 0;
          position: relative;
          z-index: 2;
        }

        .tb-card-white .tb-quote-large {
          font-size: clamp(0.95rem, 1.12vw, 1.05rem);
          line-height: 1.62;
        }

        .tb-author-row {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
          margin-top: auto;
          position: relative;
          z-index: 2;
        }

        .tb-author-name {
          font-size: clamp(1rem, 1.2vw, 1.15rem);
          font-weight: 700;
          margin: 0 0 3px 0;
          letter-spacing: -0.01em;
        }

        .tb-author-role {
          font-size: clamp(0.78rem, 0.9vw, 0.85rem);
          margin: 0;
          font-weight: 400;
        }

        /* Initial Letter Avatar box */
        .tb-avatar-letter {
          width: 52px;
          height: 52px;
          border-radius: 12px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.35rem;
          font-weight: 700;
          letter-spacing: -0.02em;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          user-select: none;
        }

        /* Flex Ratios to match exact layout */
        .tb-flex-7 {
          flex: 7;
        }

        .tb-flex-3 {
          flex: 3;
        }

        .tb-flex-1 {
          flex: 1;
        }

        @media (max-width: 1024px) {
          .tb-grid {
            grid-template-columns: 1fr;
            min-height: auto;
          }
          .tb-flex-7, .tb-flex-3, .tb-flex-1 {
            flex: auto;
          }
        }
      `}</style>

      {/* Header */}
      <div className="tb-header">
        <TimelineContent as="h2" className="tb-title" animationNum={0} customVariants={REVEAL_VARIANTS} timelineRef={containerRef}>
          Trusted by Startups and the world's <span>largest companies</span>
        </TimelineContent>
        <TimelineContent as="p" className="tb-subtitle" animationNum={1} customVariants={REVEAL_VARIANTS} timelineRef={containerRef}>
          Let's hear how Aurelix clients feel about our service & performance
        </TimelineContent>
      </div>

      {/* 3-Column Bento Grid */}
      <div className="tb-grid">
        
        {/* ── COLUMN 1 (Left: Flex 7 White Grid + Flex 3 Blue) ── */}
        <div className="tb-col">
          {/* Card 1: Thiyagavivek (White Grid Card) */}
          <TimelineContent
            animationNum={0}
            customVariants={REVEAL_VARIANTS}
            timelineRef={containerRef}
            className="tb-card tb-card-white tb-flex-7"
          >
            <div className="tb-grid-background" />
            <div style={{ marginTop: 'auto' }}>
              <p className="tb-quote tb-quote-large">
                "Aurelix didn't skim the surface. They asked the right questions, went deep on the technical side, and delivered work that most teams would have missed entirely. Technically excellent, genuinely collaborative."
              </p>
              <div className="tb-author-row">
                <div>
                  <h3 className="tb-author-name">Thiyagavivek</h3>
                  <p className="tb-author-role">Founder, Putchi</p>
                </div>
                <div className="tb-avatar-letter">T</div>
              </div>
            </div>
          </TimelineContent>

          {/* Card 2: Aditya (Blue Card) */}
          <TimelineContent
            animationNum={1}
            customVariants={REVEAL_VARIANTS}
            timelineRef={containerRef}
            className="tb-card tb-card-blue tb-flex-3"
          >
            <div>
              <p className="tb-quote">
                "We've seen incredible results with Aurelix. Their growth expertise and dedication are top tier."
              </p>
            </div>
            <div className="tb-author-row">
              <div>
                <h3 className="tb-author-name">Aditya</h3>
                <p className="tb-author-role">Founder, NuboKind</p>
              </div>
              <div className="tb-avatar-letter">A</div>
            </div>
          </TimelineContent>
        </div>

        {/* ── COLUMN 2 (Center: 3 Stacked Dark Cards) ── */}
        <div className="tb-col">
          {/* Card 3: Artkart Founder */}
          <TimelineContent
            animationNum={2}
            customVariants={REVEAL_VARIANTS}
            timelineRef={containerRef}
            className="tb-card tb-card-dark tb-flex-1"
          >
            <p className="tb-quote">
              "Their team is highly professional, and their innovative solutions have truly transformed the way we operate."
            </p>
            <div className="tb-author-row">
              <div>
                <h3 className="tb-author-name">Artkart Founder</h3>
                <p className="tb-author-role">Artkart</p>
              </div>
              <div className="tb-avatar-letter">A</div>
            </div>
          </TimelineContent>

          {/* Card 5: Sarah Lin */}
          <TimelineContent
            animationNum={4}
            customVariants={REVEAL_VARIANTS}
            timelineRef={containerRef}
            className="tb-card tb-card-dark tb-flex-1"
          >
            <p className="tb-quote">
              "Their customer support is absolutely exceptional. They are always available, incredibly proactive and helpful."
            </p>
            <div className="tb-author-row">
              <div>
                <h3 className="tb-author-name">Sarah Lin</h3>
                <p className="tb-author-role">Growth Director, E-Commerce</p>
              </div>
              <div className="tb-avatar-letter">S</div>
            </div>
          </TimelineContent>
        </div>

        {/* ── COLUMN 3 (Right: Flex 3 Blue + Flex 7 White Grid) ── */}
        <div className="tb-col">
          {/* Card 6: Marcus Vance (Blue Card) */}
          <TimelineContent
            animationNum={5}
            customVariants={REVEAL_VARIANTS}
            timelineRef={containerRef}
            className="tb-card tb-card-blue tb-flex-3"
          >
            <div>
              <p className="tb-quote">
                "Aurelix has been a key partner in our growth and omnichannel retention journey."
              </p>
            </div>
            <div className="tb-author-row">
              <div>
                <h3 className="tb-author-name">Marcus Vance</h3>
                <p className="tb-author-role">Co-Founder, Retail Systems</p>
              </div>
              <div className="tb-avatar-letter">M</div>
            </div>
          </TimelineContent>

          {/* Card 7: Elena Rostova (White Grid Card) */}
          <TimelineContent
            animationNum={6}
            customVariants={REVEAL_VARIANTS}
            timelineRef={containerRef}
            className="tb-card tb-card-white tb-flex-7"
          >
            <div className="tb-grid-background" />
            <div style={{ marginTop: 'auto' }}>
              <p className="tb-quote tb-quote-large">
                "Aurelix has been a true game-changer for us. Their exceptional service, combined with their deep expertise and commitment to excellence, has made a significant impact on our business."
              </p>
              <div className="tb-author-row">
                <div>
                  <h3 className="tb-author-name">Elena Rostova</h3>
                  <p className="tb-author-role">CTO of Spectrum Labs</p>
                </div>
                <div className="tb-avatar-letter">E</div>
              </div>
            </div>
          </TimelineContent>
        </div>

      </div>
    </div>
  );
}

export default TestimonialsBento;
